import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Button, Input } from '../../components/UI';
import ThemeToggle from '../../components/UI/ThemeToggle';
import { publicAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { sanitizePhoneInput, validateOptionalPhone, sanitizeNameInput, validateName, validateOrgName } from '../../utils/validation';
import { calculateGST as calcGST, INDIAN_STATES } from '../../utils/gst';
import {
  HiOutlineMail,
  HiOutlineLockClosed,
  HiOutlineEye,
  HiOutlineEyeOff,
  HiOutlineOfficeBuilding,
  HiOutlineUser,
  HiOutlinePhone,
  HiOutlineCheckCircle,
  HiMinusSm,
  HiPlusSm,
} from 'react-icons/hi';
import logo from '../../assets/logo.png';
import { REFERRAL_CODE_STORAGE_KEY } from './ReferralRedirect';

// Per-user pricing constants
const PRICE_PER_USER = { monthly: 599, yearly: 299 };

const CYCLES = [
  { id: 'monthly', label: 'Monthly', backend: 'monthly' },
  { id: 'yearly', label: 'Yearly', backend: 'yearly' },
];

const Register = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const planParam = searchParams.get('plan') || '';
  const cycleParam = searchParams.get('cycle') || '';
  // Primary source: sessionStorage, dropped off by /r/:code (ReferralRedirect.jsx)
  // before it hands off to this already-clean /register URL. The ?ref= query
  // param is kept only as a fallback for any already-shared old-format links —
  // scrubbed from the address bar immediately below either way, so it's never
  // visible to the person registering.
  const refParam = sessionStorage.getItem(REFERRAL_CODE_STORAGE_KEY) || searchParams.get('ref') || '';

  // The referred person shouldn't see the referral/partner code sitting in
  // their own address bar (or be able to copy/re-share that exact URL) — it's
  // captured into state below. sessionStorage itself is deliberately NOT
  // cleared here: it already survives a page refresh (only clearing the state
  // on refresh would silently drop the referral), so it's left alone until
  // registration actually succeeds (see handleSubmit's success path below).
  // The legacy ?ref= query param (old-format links) is scrubbed immediately
  // either way, since that one WOULD otherwise persist across a refresh.
  useEffect(() => {
    if (!searchParams.get('ref')) return;
    const next = new URLSearchParams(searchParams);
    next.delete('ref');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [formData, setFormData] = useState({
    orgName: '',
    orgSlug: '',
    orgEmail: '',
    orgPhone: '',
    orgState: '',
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    phone: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState(['monthly', 'yearly'].includes(cycleParam) ? cycleParam : 'monthly');
  const [numUsers, setNumUsers] = useState(1);
  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);

  // Optional referral or channel-partner code — collapsed behind a toggle link,
  // auto-filled and revealed when arriving via a shared referral link (?ref=...)
  const [showPartnerCode, setShowPartnerCode] = useState(!!refParam);
  const [partnerCode, setPartnerCode] = useState(refParam.toUpperCase());
  // When the code arrived via a shared link, lock it read-only so it can't be
  // accidentally changed — "Remove" clears it and reopens the field for manual entry.
  const [codeLockedFromLink, setCodeLockedFromLink] = useState(!!refParam);

  // Live slug availability check (runs as soon as the slug field is filled)
  const [slugStatus, setSlugStatus] = useState('idle'); // idle | checking | available | taken
  const lastSlugToastRef = useRef('');

  // OTP verification state
  const [emailVerified, setEmailVerified] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [resendTimer, setResendTimer] = useState(0);

  // Fetch available plans
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const res = await publicAPI.getPlans();
        const plansData = res.data?.data || res.data || [];
        setPlans(plansData);

        // Auto-select plan from URL param or default to full-access
        if (planParam) {
          const match = plansData.find((p) => p.slug === planParam);
          if (match) {
            setSelectedPlan(match._id);
          } else {
            // Plan slug not found — default to full-access
            const fullAccess = plansData.find((p) => p.slug === 'full-access');
            if (fullAccess) setSelectedPlan(fullAccess._id);
          }
        } else {
          // Default to full-access plan
          const fullAccess = plansData.find((p) => p.slug === 'full-access');
          if (fullAccess) setSelectedPlan(fullAccess._id);
        }
      } catch (err) {
        console.error('Failed to fetch plans:', err);
      } finally {
        setLoadingPlans(false);
      }
    };
    fetchPlans();
  }, [planParam]);

  // Auto-generate slug from org name
  // Resend OTP timer countdown
  useEffect(() => {
    if (resendTimer <= 0) return;
    const interval = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [resendTimer]);

  // Live-check slug availability while the slug field is being filled
  // (debounced 500 ms so we don't hit the API on every keystroke).
  // Covers both manual typing and the slug auto-generated from the org name.
  useEffect(() => {
    const slug = formData.orgSlug.trim().toLowerCase();
    if (!slug || slug.length < 3) {
      setSlugStatus('idle');
      return;
    }

    let cancelled = false;
    setSlugStatus('checking');
    const timer = setTimeout(async () => {
      try {
        const res = await publicAPI.checkSlug(slug);
        if (cancelled) return; // stale response — slug changed while checking
        if (res.data?.available) {
          setSlugStatus('available');
          setErrors((prev) => (prev.orgSlug ? { ...prev, orgSlug: '' } : prev));
        } else {
          setSlugStatus('taken');
          setErrors((prev) => ({
            ...prev,
            orgSlug: 'This organization slug is already taken. Please choose another one',
          }));
          // Popup only once per distinct slug value
          if (lastSlugToastRef.current !== slug) {
            lastSlugToastRef.current = slug;
            toast.error('This organization slug is already taken. Please choose another one');
          }
        }
      } catch {
        // Check endpoint failed — don't block the user, backend re-validates on register
        if (!cancelled) setSlugStatus('idle');
      }
    }, 500);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [formData.orgSlug]);

  // Send OTP to email
  const handleSendOtp = async () => {
    // Validate email first
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setErrors((prev) => ({ ...prev, email: 'Please enter a valid email address' }));
      return;
    }

    setOtpSending(true);
    setOtpError('');
    try {
      const res = await publicAPI.sendRegistrationOTP(formData.email.trim().toLowerCase());
      if (res.data?.success) {
        setShowOtpModal(true);
        setOtp('');
        setResendTimer(60); // 60 second cooldown
        toast.success('OTP sent to your email');
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to send OTP. Please try again.';
      setOtpError(msg);
      toast.error(msg);
    } finally {
      setOtpSending(false);
    }
  };

  // Verify OTP
  const handleVerifyOtp = async () => {
    if (!otp || otp.length !== 6) {
      setOtpError('Please enter the 6-digit OTP');
      return;
    }

    setOtpVerifying(true);
    setOtpError('');
    try {
      const res = await publicAPI.verifyRegistrationOTP(formData.email.trim().toLowerCase(), otp);
      if (res.data?.success) {
        setEmailVerified(true);
        setShowOtpModal(false);
        setErrors((prev) => ({ ...prev, email: '' }));
        toast.success('Email verified successfully!');
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'Invalid OTP. Please try again.';
      setOtpError(msg);
      toast.error(msg);
    } finally {
      setOtpVerifying(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value };
      if (field === 'orgName' && !slugManuallyEdited) {
        updated.orgSlug = value
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-|-$/g, '');
      }
      if (field === 'orgSlug') {
        setSlugManuallyEdited(true);
      }
      // Reset email verification when email changes
      if (field === 'email') {
        setEmailVerified(false);
      }
      return updated;
    });
    // Clear the error for this field when the user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const selectedPlanData = plans.find((p) => p._id === selectedPlan);
  const isPerUser = selectedPlanData?.isPerUser;
  // Plan types: Free · 3-Day / 5-Day Trial (₹0 with trialDays) · paid (no trial)
  const isPaidPlanData = (p) => !!p && (p.isPerUser || p.price?.monthly > 0 || p.price?.yearly > 0);
  const isFreePlan = selectedPlanData?.slug === 'free';
  const isTrialPlan = !!selectedPlanData && !isPaidPlanData(selectedPlanData) && selectedPlanData.trialDays > 0;
  const trialDays = selectedPlanData?.trialDays || 0;

  // Calculate pricing
  const perUserPrice = isPerUser ? PRICE_PER_USER[billingCycle] : 0;
  const subtotal = isPerUser ? perUserPrice * numUsers * (billingCycle === 'yearly' ? 12 : 1) : 0;
  // GST based on selected state (default to IGST if no state selected)
  const registerGstConfig = 'applicable';
  const registerOrgState = formData.orgState || null;
  const gstResult = calcGST(subtotal, registerGstConfig, registerOrgState);
  const gstAmount = gstResult.gstAmount;
  const cgstAmount = gstResult.cgstAmount;
  const sgstAmount = gstResult.sgstAmount;
  const totalAmount = gstResult.totalAmount;
  const gstType = gstResult.gstType;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};

    // Validation
    const orgNameValidation = validateOrgName(formData.orgName);
    if (!orgNameValidation.isValid) {
      newErrors.orgName = orgNameValidation.error;
    }
    if (!formData.orgSlug.trim() || formData.orgSlug.length < 3) {
      newErrors.orgSlug = 'Organization slug must be at least 3 characters';
    } else if (formData.orgSlug.length > 50) {
      newErrors.orgSlug = 'Organization slug must be 50 characters or less';
    } else if (slugStatus === 'taken') {
      newErrors.orgSlug = 'This organization slug is already taken. Please choose another one';
    }
    const firstNameValidation = validateName(formData.firstName, 'First name');
    if (!firstNameValidation.isValid) {
      newErrors.firstName = firstNameValidation.error;
    }
    const lastNameValidation = validateName(formData.lastName, 'Last name');
    if (!lastNameValidation.isValid) {
      newErrors.lastName = lastNameValidation.error;
    }
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    } else if (formData.email.trim().length > 254) {
      newErrors.email = 'Email must be 254 characters or less';
    }
    if (!formData.password || formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else if (formData.password.length > 128) {
      newErrors.password = 'Password must be 128 characters or less';
    }
    // Phone is optional, but if provided it must be valid
    if (formData.phone.trim()) {
      const phoneValidation = validateOptionalPhone(formData.phone);
      if (!phoneValidation.isValid) {
        newErrors.phone = phoneValidation.error;
      }
    }
    if (!selectedPlan) {
      newErrors.plan = 'Please select a plan';
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      // Scroll to the first field with an error
      const firstErrorField = Object.keys(newErrors)[0];
      const fieldMap = {
        orgName: 'orgName',
        orgSlug: 'orgSlug',
        firstName: 'firstName',
        lastName: 'lastName',
        email: 'email',
        password: 'password',
        phone: 'phone',
      };
      if (fieldMap[firstErrorField]) {
        document.querySelector(`[name="${fieldMap[firstErrorField]}"]`)?.focus();
      }
      return;
    }

    // Email verification is required before registration
    if (!emailVerified) {
      setErrors({ ...newErrors, email: 'Please verify your email address before registering' });
      return;
    }

    setIsLoading(true);

    try {
      // Final slug re-check before registering (covers the race where the slug
      // was taken while the rest of the form was being filled in)
      const slugCheck = await publicAPI.checkSlug(formData.orgSlug.trim().toLowerCase());
      if (slugCheck.data?.available === false) {
        setSlugStatus('taken');
        const msg = 'This organization slug is already taken. Please choose another one';
        setErrors({ orgSlug: msg });
        toast.error(msg);
        document.querySelector('[name="orgSlug"]')?.focus();
        return;
      }

      const registerData = {
        orgName: formData.orgName.trim(),
        orgSlug: formData.orgSlug.trim().toLowerCase(),
        orgEmail: formData.email.trim().toLowerCase(),
        orgPhone: formData.orgPhone.trim() || undefined,
        state: formData.orgState || undefined,
        planId: selectedPlan,
        billingCycle,
        numUsers: isPerUser ? numUsers : 1,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
        phone: formData.phone.trim() || undefined,
        emailVerified: true,
        referralOrPartnerCode: partnerCode.trim() || undefined,
      };

      // Register account first
      const res = await publicAPI.register(registerData);
      const data = res.data?.data || res.data;

      // Registration succeeded — the referral code (if any) has been submitted
      // and attributed server-side, so it no longer needs to linger in this tab.
      sessionStorage.removeItem(REFERRAL_CODE_STORAGE_KEY);

      if (data?.token) {
        localStorage.setItem('admin_auth_token', data.token);
        localStorage.setItem('admin_user_data', JSON.stringify(data.user));

        // Paid plans have no trial — the panel opens on the "Complete Your
        // Payment" screen and the subscription is active as soon as it is paid.
        // Full page load so AuthProvider picks up the new token.
        window.location.href = '/app/';
      } else {
        window.location.href = '/login';
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Registration failed. Please try again.';
      if (/partner code/i.test(msg)) {
        setErrors({ partnerCode: msg });
      } else {
        setErrors({ form: msg });
      }
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-primary-600 via-indigo-700 to-primary-800 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-48 sm:w-72 md:w-96 h-48 sm:h-72 md:h-96 bg-white/10 rounded-full blur-3xl transform -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-64 sm:w-80 md:w-[500px] h-64 sm:h-80 md:h-[500px] bg-indigo-400/20 rounded-full blur-3xl transform translate-x-1/3 translate-y-1/3" />
        <div className="absolute top-1/2 left-1/2 w-40 sm:w-56 md:w-64 h-40 sm:h-56 md:h-64 bg-primary-300/10 rounded-full blur-3xl transform -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
      </div>

      {/* Content */}
      <div className="relative flex-1 flex flex-col justify-center px-4 py-8 sm:py-12">
        {/* Dark / light mode toggle — moon = dark, sun = light (same as Login) */}
        <div className="absolute top-4 right-4">
          <ThemeToggle />
        </div>
        <div className="mx-auto w-full max-w-lg">
          {/* Logo */}
          <div className="text-center mb-6 sm:mb-8">
            <div className="brand-logo-box inline-flex items-center justify-center lg:w-[300px] lg:h-[80px] w-60 rounded-2xl bg-white backdrop-blur-sm mb-4 shadow-2xl ring-1 ring-white/20 overflow-hidden">
              <img src={logo} alt="TeleValley" className="w-full h-full object-contain p-1.5 sm:p-2" />
            </div>
          </div>

          {/* Plan & Pricing Banner */}
          {selectedPlanData && (
            <div className="mb-4 p-3 sm:p-4 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-white/60 font-medium">Selected Plan</p>
                  <p className="text-white font-bold">{selectedPlanData.name}</p>
                  <p className="text-xs text-white/70">
                    {isFreePlan ? 'Free forever' : isTrialPlan ? `${trialDays}-day free trial` : isPerUser ? `${numUsers} user${numUsers > 1 ? 's' : ''} · Per-user pricing` : selectedPlanData.description}
                  </p>
                </div>
                <div className="text-right">
                  {isFreePlan ? (
                    <p className="text-lg font-bold text-white">Free</p>
                  ) : isPerUser ? (
                    <>
                      <p className="text-lg font-bold text-white">₹{perUserPrice.toLocaleString('en-IN')}/mo</p>
                      <p className="text-xs text-white/70">per user</p>
                    </>
                  ) : isTrialPlan ? (
                    <p className="text-lg font-bold text-white">Free Trial</p>
                  ) : (
                    <p className="text-lg font-bold text-white">₹{selectedPlanData.price?.monthly?.toLocaleString('en-IN') || '0'}/mo</p>
                  )}
                </div>
              </div>

              {/* Per-user pricing controls */}
              {isPerUser && (
                <div className="mt-3 pt-3 border-t border-white/20">
                  {/* Billing cycle */}
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-white/70">Billing Cycle</span>
                    <div className="flex gap-1.5">
                      {CYCLES.map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => setBillingCycle(c.id)}
                          className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all ${
                            billingCycle === c.id
                              ? 'bg-white text-primary-700'
                              : 'bg-white/15 text-white hover:bg-white/25'
                          }`}
                        >
                          {c.label}
                          {c.id === 'yearly' && <span className="ml-1 text-[10px] text-green-300">50% off</span>}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* User count */}
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-white/70">Users</span>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setNumUsers(Math.max(1, numUsers - 1))}
                        disabled={numUsers <= 1}
                        className="flex h-7 w-7 items-center justify-center rounded-md bg-white/15 text-white hover:bg-white/25 disabled:opacity-40"
                      >
                        <HiMinusSm className="h-3 w-3" />
                      </button>
                      <span className="text-sm font-bold text-white min-w-[20px] text-center">{numUsers}</span>
                      <button
                        type="button"
                        onClick={() => setNumUsers(Math.min(100, numUsers + 1))}
                        disabled={numUsers >= 100}
                        className="flex h-7 w-7 items-center justify-center rounded-md bg-white/15 text-white hover:bg-white/25 disabled:opacity-40"
                      >
                        <HiPlusSm className="h-3 w-3" />
                      </button>
                    </div>
                  </div>

                  {/* Total */}
                  <div className="mt-2 pt-2 border-t border-white/10">
                    <div className="flex items-center justify-between text-xs text-white/60">
                      <span>{billingCycle === 'yearly' ? `${perUserPrice.toLocaleString('en-IN')}/user/mo × ${numUsers} user${numUsers > 1 ? 's' : ''} × 12 mo` : `${perUserPrice.toLocaleString('en-IN')}/user × ${numUsers} user${numUsers > 1 ? 's' : ''}`}</span>
                      <span>₹{subtotal.toLocaleString('en-IN')}/{billingCycle === 'yearly' ? 'yr' : 'mo'}</span>
                    </div>
                    <div className="flex items-center justify-between mt-1" style={{ fontWeight: '700', fontSize: '14px' }}>
                      <span className="text-white/80">Total</span>
                      <span className="text-white">
                        ₹{totalAmount.toLocaleString('en-IN')}/{billingCycle === 'yearly' ? 'yr' : 'mo'}
                        {gstAmount > 0 && <span className="text-xs font-normal text-white/50"> incl. GST</span>}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Registration Card */}
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl sm:rounded-3xl shadow-2xl p-5 sm:p-8 ring-1 ring-white/20">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 text-center mb-2">
              Create Your Account
            </h2>
            <p className="text-center text-gray-500 text-sm mb-6 sm:mb-8">
              {!isFreePlan && !isTrialPlan ? 'Create your account, then choose your subscription' : 'Start using TeleValley in minutes'}
            </p>

            {errors.form && (
              <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-300">
                <p className="text-sm text-red-700 text-center font-medium">{errors.form}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
              {/* Plan Selector (if no plan pre-selected) */}
              {!planParam && plans.length > 0 && (
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-2">Select a Plan</label>
                  {errors.plan && <p className="text-xs text-danger-600 font-medium mb-2">{errors.plan}</p>}
                  <div className="grid grid-cols-1 gap-2">
                    {plans
                      .map((p) => (
                        <button
                          key={p._id}
                          type="button"
                          onClick={() => { setSelectedPlan(p._id); setNumUsers(1); if (errors.plan) setErrors((prev) => ({ ...prev, plan: '' })); }}
                          className={`flex items-center justify-between p-3 rounded-xl border-2 text-left transition-all ${
                            selectedPlan === p._id
                              ? 'border-primary-500 bg-primary-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div>
                            <p className="font-semibold text-gray-900 text-sm">{p.name}</p>
                            <p className="text-xs text-gray-500">{p.description}</p>
                          </div>
                          <p className="font-bold text-primary-600 text-sm">
                            {p.isPerUser ? `₹${p.pricePerUser?.monthly?.toLocaleString('en-IN') || '599'}/user/mo` :
                             p.price?.monthly === 0 ? (p.trialDays > 0 ? `${p.trialDays}-day free trial` : 'Free') : `₹${p.price?.monthly?.toLocaleString('en-IN')}/mo`}
                          </p>
                        </button>
                      ))}
                  </div>
                </div>
              )}

              {/* Hidden plan selector when pre-selected */}
              {planParam && selectedPlan && (
                <input type="hidden" value={selectedPlan} />
              )}

              {/* Organization Section */}
              <div className="pt-2">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Organization Details</p>
                <div className="space-y-3">
                  <Input
                    label="Organization Name"
                    type="text"
                    name="orgName"
                    required
                    maxLength={100}
                    value={formData.orgName}
                    onChange={(e) => handleChange('orgName', e.target.value)}
                    placeholder="Acme Sales Pvt Ltd"
                    icon={<HiOutlineOfficeBuilding className="w-5 h-5" />}
                    error={errors.orgName}
                    disabled={isLoading}
                  />
                  <Input
                    label="Organization Slug"
                    type="text"
                    name="orgSlug"
                    required
                    maxLength={50}
                    value={formData.orgSlug}
                    onChange={(e) => handleChange('orgSlug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                    placeholder="acme-sales"
                    error={errors.orgSlug}
                    disabled={isLoading}
                  />
                  {/* Live slug availability feedback */}
                  {slugStatus === 'checking' && !errors.orgSlug && (
                    <p className="mt-1 text-xs text-gray-500">Checking slug availability...</p>
                  )}
                  {slugStatus === 'available' && !errors.orgSlug && (
                    <p className="mt-1 text-xs text-green-600 flex items-center gap-1">
                      <HiOutlineCheckCircle className="w-4 h-4" />
                      This organization slug is available
                    </p>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      State <span className="text-gray-400 text-xs">(for GST calculation)</span>
                    </label>
                    <select
                      name="orgState"
                      value={formData.orgState}
                      onChange={(e) => handleChange('orgState', e.target.value)}
                      className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm shadow-sm transition-colors focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:bg-gray-50 disabled:text-gray-400"
                      disabled={isLoading}
                    >
                      <option value="">Select your state</option>
                      {INDIAN_STATES.map((state) => (
                        <option key={state} value={state}>{state}</option>
                      ))}
                    </select>
                  </div>

                  {/* Referral or channel-partner code — collapsed by default, unless
                      arriving via a shared referral link (?ref=...). When it arrived
                      via a link, nothing renders here at all (not even a confirmation
                      badge) — the referred person shouldn't be able to tell referral
                      tracking is happening. The code is just silently carried through
                      to submission in `partnerCode`. */}
                  {!showPartnerCode || codeLockedFromLink ? (
                    codeLockedFromLink ? null : (
                      <button
                        type="button"
                        onClick={() => setShowPartnerCode(true)}
                        className="text-xs font-semibold text-primary-600 hover:text-primary-700"
                        disabled={isLoading}
                      >
                        Have a referral or partner code?
                      </button>
                    )
                  ) : (
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="block text-sm font-medium text-gray-700">
                          Referral / Partner Code <span className="text-gray-400 text-xs">(optional)</span>
                        </label>
                      </div>
                      <input
                        type="text"
                        name="partnerCode"
                        value={partnerCode}
                        onChange={(e) => {
                          setPartnerCode(e.target.value.toUpperCase());
                          if (errors.partnerCode) setErrors((prev) => ({ ...prev, partnerCode: '' }));
                        }}
                        placeholder="e.g. REF-4F82A1 or CP-4F82A1"
                        disabled={isLoading}
                        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm shadow-sm transition-colors focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:bg-gray-50 disabled:text-gray-400"
                      />
                      {errors.partnerCode && (
                        <p className="mt-1 text-xs text-danger-600 font-medium">{errors.partnerCode}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Personal Section */}
              <div className="pt-2">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Your Details</p>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      label="First Name"
                      type="text"
                      name="firstName"
                      required
                      maxLength={50}
                      value={formData.firstName}
                      onChange={(e) => handleChange('firstName', sanitizeNameInput(e.target.value))}
                      placeholder="Rohit"
                      icon={<HiOutlineUser className="w-5 h-5" />}
                      error={errors.firstName}
                      disabled={isLoading}
                    />
                    <Input
                      label="Last Name"
                      type="text"
                      name="lastName"
                      required
                      maxLength={50}
                      value={formData.lastName}
                      onChange={(e) => handleChange('lastName', sanitizeNameInput(e.target.value))}
                      placeholder="Sharma"
                      error={errors.lastName}
                      disabled={isLoading}
                    />
                  </div>
                  <div>
                    <div className="flex items-end gap-2">
                      <div className="flex-1">
                        <Input
                          label="Email Address"
                          type="email"
                          name="email"
                          required
                          maxLength={254}
                          value={formData.email}
                          onChange={(e) => handleChange('email', e.target.value)}
                          placeholder="rohit@company.com"
                          icon={<HiOutlineMail className="w-5 h-5" />}
                          error={errors.email}
                          disabled={isLoading}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={handleSendOtp}
                        disabled={otpSending || !formData.email.trim() || emailVerified}
                        className="mb-0.5 px-3 py-2.5 rounded-xl text-sm font-semibold transition-all whitespace-nowrap shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{
                          backgroundColor: emailVerified ? '#10b981' : '#4f46e5',
                          color: 'white',
                          border: 'none',
                          height: '46px',
                        }}
                      >
                        {emailVerified ? (
                          <span className="flex items-center gap-1">
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                            Verified
                          </span>
                        ) : otpSending ? (
                          'Sending...'
                        ) : resendTimer > 0 ? (
                          `Resend (${resendTimer}s)`
                        ) : (
                          'Verify Email'
                        )}
                      </button>
                    </div>
                    {emailVerified && (
                      <p className="mt-1 text-xs text-green-600 font-medium flex items-center gap-1">
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                        Email verified successfully
                      </p>
                    )}
                  </div>
                  <Input
                    label="Phone Number (Optional)"
                    type="tel"
                    name="phone"
                    maxLength={20}
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', sanitizePhoneInput(e.target.value))}
                    placeholder="+91 9876543210"
                    icon={<HiOutlinePhone className="w-5 h-5" />}
                    error={errors.phone}
                    disabled={isLoading}
                  />
                  <Input
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    required
                    maxLength={128}
                    value={formData.password}
                    onChange={(e) => handleChange('password', e.target.value)}
                    placeholder="Min 8 characters"
                    icon={<HiOutlineLockClosed className="w-5 h-5" />}
                    error={errors.password}
                    disabled={isLoading}
                    rightIcon={
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="text-gray-400 hover:text-gray-600 p-1 focus:outline-none"
                      >
                        {showPassword ? <HiOutlineEyeOff className="w-5 h-5" /> : <HiOutlineEye className="w-5 h-5" />}
                      </button>
                    }
                  />
                </div>
              </div>

              <Button type="submit" variant="primary" size="lg" className="w-full" loading={isLoading} disabled={isLoading}>
                {isLoading ? 'Creating Account...' : !isFreePlan && !isTrialPlan ? 'Create Account & Get Started' : 'Create Account & Get Started'}
              </Button>
            </form>

            {/* Login Link */}
            <div className="mt-5 text-center">
              <p className="text-sm text-gray-600">
                Already have an account?{' '}
                <Link to="/app/" className="font-semibold text-primary-600 hover:text-primary-700">
                  Sign in
                </Link>
              </p>
            </div>
          </div>

          {/* Bottom */}
          <div className="mt-6 sm:mt-8 text-center">
            <p className="text-xs text-white/50">
              © {new Date().getFullYear()} TeleValley. All rights reserved.
            </p>
          </div>
        </div>
      </div>

      {/* OTP Verification Modal */}
      {showOtpModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8 max-w-sm w-full">
            <div className="text-center mb-6">
              <div className="mx-auto w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center mb-4">
                <HiOutlineMail className="w-6 h-6 text-primary-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">Verify Your Email</h3>
              <p className="text-sm text-gray-500 mt-1">
                We sent a 6-digit code to<br />
                <span className="font-semibold text-gray-700">{formData.email}</span>
              </p>
            </div>

            {otpError && (
              <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200">
                <p className="text-sm text-red-700 text-center">{otpError}</p>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Enter OTP</label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={otp}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setOtp(val);
                    if (otpError) setOtpError('');
                  }}
                  className="w-full text-center text-2xl tracking-[0.5em] font-bold py-3 px-4 border-2 border-gray-200 rounded-xl focus:border-primary-500 focus:ring-2 focus:ring-primary-200 outline-none transition-all"
                  placeholder="000000"
                  autoFocus
                />
              </div>

              <button
                type="button"
                onClick={handleVerifyOtp}
                disabled={otpVerifying || otp.length !== 6}
                className="w-full py-3 px-4 rounded-xl text-white font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: '#4f46e5' }}
              >
                {otpVerifying ? 'Verifying...' : 'Verify OTP'}
              </button>

              <div className="text-center">
                {resendTimer > 0 ? (
                  <p className="text-sm text-gray-500">
                    Resend OTP in <span className="font-semibold text-gray-700">{resendTimer}s</span>
                  </p>
                ) : (
                  <button
                    type="button"
                    onClick={handleSendOtp}
                    disabled={otpSending}
                    className="text-sm font-semibold text-primary-600 hover:text-primary-700 disabled:opacity-50"
                  >
                    {otpSending ? 'Sending...' : 'Resend OTP'}
                  </button>
                )}
              </div>

              <button
                type="button"
                onClick={() => { setShowOtpModal(false); setOtp(''); setOtpError(''); }}
                className="w-full py-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Register;